import AutoComplete from "antd/lib/auto-complete";
import classNames from "classnames";
import { first, isEmpty } from "lodash";
import { ChangeEvent, useState } from "react";
import { usAutocomplete, usStreet } from "smartystreets-javascript-sdk";

import InputWrapper from "rivals/components/shared/Form/Input";
import useSmartyStreets from "rivals/shared/hooks/useSmartyStreets";
import styles from "./Address.module.scss";
import { OptionValue, OwnProps } from "./types";

function AddressAutoComplete({
  className,
  data = "",
  feedback = "",
  htmlFor = "",
  label = "",
  onBlur,
  onChange: onChangeProp,
  onSelect: onSelectProp,
  token,
  value
}: OwnProps): JSX.Element | null {
  const [options, setOptions] = useState<OptionValue[]>([]);
  const { autoCompleteClient, usStreetClient } = useSmartyStreets(token);

  const formatResults = (
    resultsSet: usAutocomplete.Suggestion[]
  ): OptionValue[] =>
    resultsSet.map(
      (result): OptionValue => ({
        city: result.city,
        state: result.state,
        streetline: result.streetLine,
        value: result.text
      })
    );

  const inputClasses = classNames(styles.autoComplete, {
    [styles.feedback]: feedback
  });

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChangeProp(e);
    if (e.target.value.length) {
      const lookup = new usAutocomplete.Lookup(e.target.value);
      lookup.geolocate = true;
      lookup.geolocatePrecision = "state";
      autoCompleteClient?.current?.send(lookup).then(res => {
        setOptions(formatResults(res.result));
      });
    }
  };

  /**
   * Selects an item from the AutoComplete results, then validates it with SmartyStreets.
   * If the address is valid, we update the BillingForm.
   *
   * "invalid" is the most permissive match, this will always return at least one result even
   * if the address is invalid.
   */
  const onSelect = (_value: string, option: OptionValue): void => {
    const { city, state, streetline: streetLine } = option;
    const lookup = new usStreet.Lookup();
    lookup.street = streetLine;
    lookup.city = city;
    lookup.state = state;
    lookup.zipCode = "";
    lookup.match = "invalid";
    usStreetClient?.current?.send(lookup).then(({ lookups }) => {
      if (!isEmpty(lookups)) {
        const candidate = first(lookups[0].result);
        if (candidate) {
          const billingFields = {
            city: { error: "", value: candidate.components.cityName || "" },
            state: { error: "", value: candidate.components.state || "" },
            street1: { error: "", value: candidate.deliveryLine1 || "" },
            street2: { error: "", value: candidate.deliveryLine2 || "" },
            zip: { error: "", value: candidate.components.zipCode || "" }
          };
          onSelectProp(billingFields);
        }
      }
    });
  };

  return (
    <InputWrapper
      className={className}
      data={data}
      feedback={feedback}
      htmlFor={htmlFor}
      label={label}
    >
      <AutoComplete
        className={inputClasses}
        onBlur={onBlur}
        onSelect={onSelect}
        options={options}
        value={value as string}
      >
        <input name={htmlFor} onChange={onChange} type="search" />
      </AutoComplete>
    </InputWrapper>
  );
}

export default AddressAutoComplete;
